/*
PROTOTYPESPARIS - SIDE MENU INTERACTIONS
Compatible avec Shopify Vessel 2.1.1
Gestion complète : ouverture/fermeture, focus trap, coordination drawers, accessibilité
*/

/**
 * Classe principale pour gérer le menu latéral PrototypesParis
 * 
 * @class PrototypesSideMenu
 * @description Gère l'ouverture/fermeture du menu latéral avec :
 * - Focus trap (navigation clavier)
 * - Coordination avec les drawers Vessel
 * - Accessibilité complète (ARIA, navigation clavier)
 * - Animations fluides
 */
class PrototypesSideMenu {
  /**
   * Crée une instance du menu latéral
   * @constructor
   */
  constructor() {
    this.burgerMenu = document.getElementById('ppBurgerMenu');
    this.sidebarMenu = document.getElementById('ppSidebarMenu');
    this.menuClose = document.getElementById('ppMenuClose');
    this.menuOverlay = document.getElementById('ppMenuOverlay');
    this.menuItems = document.querySelectorAll('.pp-menu-item');
    this.isOpen = false;
    this.focusableElements = [];
    this.lastFocusedElement = null;
    this.scrollY = 0;

    this.init();
  }

  /**
   * Initialise le menu (vérifie les éléments et bind les événements)
   * @method init
   * @returns {void}
   */
  init() {
    if (!this.burgerMenu || !this.sidebarMenu) {
      console.warn('PrototypesParis: Menu elements not found');
      return;
    }

    this.bindEvents();
    this.setupFocusableElements();
  }

  /**
   * Lie tous les événements (clics, touches clavier, etc.)
   * @method bindEvents
   * @returns {void}
   */
  bindEvents() {
    // OUVERTURE PAR BURGER
    this.burgerMenu.addEventListener('click', (e) => {
      e.preventDefault();
      this.openMenu();
    });

    // FERMETURE PAR BOUTON X
    this.menuClose.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.closeMenu();
    });

    // FERMETURE PAR CLICK-OUTSIDE
    this.menuOverlay.addEventListener('click', () => {
      this.closeMenu();
    });

    // FERMETURE PAR ÉCHAP
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeMenu();
      }
      
      // GESTION FOCUS TRAP
      if (this.isOpen && e.key === 'Tab') {
        this.handleTabKey(e);
      }
    });

    // PRÉVENIR LA FERMETURE LORS DU CLIC SUR LE MENU
    this.sidebarMenu.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    // LIENS CLIQUABLES (navigation)
    this.menuItems.forEach(item => {
      item.addEventListener('click', (e) => {
        // Les liens sociaux s'ouvrent en nouvel onglet (déjà géré dans le HTML)
        // Les autres liens naviguent dans le même onglet
        if (!item.target || item.target !== '_blank') {
          // Fermer le menu après navigation
          setTimeout(() => {
            this.closeMenu();
          }, 100);
        }
      });
    });
  }

  /**
   * Configure la liste des éléments focusables pour le focus trap
   * @method setupFocusableElements
   * @returns {void}
   * @description Identifie tous les éléments interactifs du menu pour la navigation clavier
   */
  setupFocusableElements() {
    const focusableSelectors = [
      '.pp-menu-close',
      '.pp-menu-item',
      '.pp-social-icon'
    ].join(', ');

    this.focusableElements = Array.from(
      this.sidebarMenu.querySelectorAll(focusableSelectors)
    ).filter(el => !el.disabled && el.offsetParent !== null);
  }

  /**
   * Ouvre le menu latéral
   * @method openMenu
   * @returns {void}
   * @description 
   * - Ferme les autres drawers Vessel
   * - Stocke l'élément ayant le focus
   * - Active les classes CSS pour l'animation
   * - Verrouille le scroll de la page
   * - Place le focus sur le bouton fermer
   */
  openMenu() {
    if (this.isOpen) return;

    // FERMER LES AUTRES DRAWERS DE VESSEL
    this.closeOtherDrawers();

    // STOCKER L'ÉLÉMENT AYANT LE FOCUS
    this.lastFocusedElement = document.activeElement;

    // OUVRIR LE MENU
    this.burgerMenu.classList.add('active');
    this.sidebarMenu.classList.add('active');
    this.menuOverlay.classList.add('active');
    
    // SCROLL LOCK
    this.scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    document.body.style.setProperty('--pp-scroll-lock-top', `-${this.scrollY}px`);
    document.body.classList.add('pp-scroll-locked');
    
    this.isOpen = true;

    // FOCUS SUR LE BOUTON FERMER
    setTimeout(() => {
      this.menuClose.focus();
    }, 100);

    // SETUP FOCUS TRAP
    this.setupFocusableElements();
  }

  /**
   * Ferme le menu latéral
   * @method closeMenu
   * @returns {void}
   * @description 
   * - Ajoute l'animation de fermeture
   * - Retire les classes actives après l'animation
   * - Déverrouille le scroll de la page
   * - Restaure le focus sur l'élément précédent
   */
  closeMenu() {
    if (!this.isOpen) return;

    // ANIMATION DE FERMETURE
    this.sidebarMenu.classList.add('closing');
    
    setTimeout(() => {
      this.burgerMenu.classList.remove('active');
      this.sidebarMenu.classList.remove('active', 'closing');
      this.menuOverlay.classList.remove('active');
      
      // RETIRER SCROLL LOCK
      document.body.classList.remove('pp-scroll-locked');
      document.body.style.removeProperty('--pp-scroll-lock-top');
      window.scrollTo(0, this.scrollY);
      this.scrollY = 0;
      
      this.isOpen = false;

      // RETOUR DU FOCUS
      if (this.lastFocusedElement && this.lastFocusedElement.focus) {
        this.lastFocusedElement.focus();
      } else {
        this.burgerMenu.focus();
      }
    }, 300);
  }

  /**
   * Ferme les autres drawers Vessel (panier, recherche) pour éviter les conflits
   * @method closeOtherDrawers
   * @returns {void}
   * @description Ferme automatiquement le panier et la recherche si ouverts
   */
  closeOtherDrawers() {
    // FERMER LE PANIER VESSEL
    const cartDrawer = document.querySelector('[data-section-type="cart-drawer"]');
    const cartOverlay = document.querySelector('.cart-drawer-overlay');
    const cartDrawerEl = document.querySelector('.cart-drawer');
    
    if (cartDrawer) {
      cartDrawer.classList.remove('cart-drawer--open');
    }
    if (cartOverlay) {
      cartOverlay.classList.remove('active');
    }
    if (cartDrawerEl) {
      cartDrawerEl.classList.remove('open');
    }

    // FERMER LA RECHERCHE VESSEL
    const searchModal = document.querySelector('.search-modal');
    const searchOverlay = document.querySelector('.search-modal-overlay');
    
    if (searchModal) {
      searchModal.classList.remove('search-modal--open');
    }
    if (searchOverlay) {
      searchOverlay.classList.remove('active');
    }

    // DÉCLENCHER L'ÉVÉNEMENT DE FERMETURE VESSEL SI DISPONIBLE
    if (window.theme && window.theme.DrawerManager) {
      window.theme.DrawerManager.closeAll();
    }
  }

  /**
   * Gère la navigation au clavier avec Tab (focus trap)
   * @method handleTabKey
   * @param {KeyboardEvent} e - L'événement clavier
   * @returns {void}
   * @description 
   * - Empêche le focus de sortir du menu
   * - Tab : boucle du dernier au premier élément
   * - Shift+Tab : boucle du premier au dernier élément
   */
  handleTabKey(e) {
    if (this.focusableElements.length === 0) return;

    const firstElement = this.focusableElements[0];
    const lastElement = this.focusableElements[this.focusableElements.length - 1];

    if (e.shiftKey) {
      // TAB + SHIFT (vers l'arrière)
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      // TAB (vers l'avant)
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }

  // ========== MÉTHODES PUBLIQUES POUR INTÉGRATION ==========
  
  /**
   * Alterne l'état du menu (ouvre si fermé, ferme si ouvert)
   * @method toggle
   * @returns {void}
   * @example
   * // Depuis la console ou un autre script
   * window.prototypesMenu.toggle();
   */
  toggle() {
    if (this.isOpen) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  /**
   * Ferme le menu (méthode publique)
   * @method close
   * @returns {void}
   * @example
   * window.prototypesMenu.close();
   */
  close() {
    this.closeMenu();
  }

  /**
   * Ouvre le menu (méthode publique)
   * @method open
   * @returns {void}
   * @example
   * window.prototypesMenu.open();
   */
  open() {
    this.openMenu();
  }
}

// ========== INITIALISATION AUTOMATIQUE ==========
/**
 * Initialise automatiquement le menu au chargement de la page
 * Le menu est accessible via window.prototypesMenu
 */
document.addEventListener('DOMContentLoaded', () => {
  window.prototypesMenu = new PrototypesSideMenu();
});

// ========== COORDINATION AVEC LES DRAWERS VESSEL ==========
/**
 * Ferme automatiquement le menu PrototypesParis quand un drawer Vessel s'ouvre
 * Évite les conflits visuels entre plusieurs overlays
 */
document.addEventListener('vessel:drawer:open', () => {
  if (window.prototypesMenu && window.prototypesMenu.isOpen) {
    window.prototypesMenu.close();
  }
});

// ========== GESTION DU RESIZE ==========
/**
 * Recalcule les éléments focusables lors du redimensionnement
 * Important pour le focus trap sur mobile/desktop
 */
window.addEventListener('resize', () => {
  if (window.prototypesMenu) {
    window.prototypesMenu.setupFocusableElements();
  }
});

// ========== EXPORT POUR USAGE EXTERNE ==========
/**
 * Export pour utilisation dans d'autres modules (si nécessaire)
 */
if (typeof module !== 'undefined' && module.exports) {
  module.exports = PrototypesSideMenu;
}
